import { useEffect, useState } from "react";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, AlertCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const MUTED = "#94a3b8";
const BORDER = "rgba(99,102,241,0.18)";

export default function AuthVerify() {
  const [status, setStatus] = useState<"loading" | "error">("loading");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");
    const rawReturnTo = params.get("returnTo");
    // Security: only allow relative paths — reject http://, //, or anything with a colon
    const returnTo = (rawReturnTo && rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//") && !rawReturnTo.includes(":"))
      ? rawReturnTo
      : null;

    if (error === "expired" || !token) {
      setStatus("error");
      return;
    }

    // Use fetch so the session cookie is set while client code keeps running.
    // The server 302s to /agent/dashboard — fetch follows it, setting the cookie.
    fetch(`/api/auth/verify?token=${encodeURIComponent(token)}`, {
      credentials: "same-origin",
    })
      .then(async (res) => {
        // If the server redirected back to an error page, treat as expired.
        if (res.url.includes("/register?error=expired")) {
          setStatus("error");
          return;
        }

        // Session cookie is now active. Check for a pending checkout plan.
        const pendingPlan = sessionStorage.getItem("pendingPlan");
        if (pendingPlan) {
          sessionStorage.removeItem("pendingPlan");
          try {
            const checkoutRes = await apiRequest("POST", "/api/billing/checkout", { planKey: pendingPlan, billingCycle: "monthly", currency: "nzd" });
            if (checkoutRes.ok) {
              const data = await checkoutRes.json();
              if (data.url) {
                window.location.href = data.url;
                return;
              }
            }
          } catch {
            // Checkout failed — fall through to dashboard
          }
        }

        // Navigate to returnTo if present and safe, otherwise fall back to agent dashboard.
        // ADMIN users must use /ops to access the mother dashboard.
        window.location.href = returnTo ?? "/agent/dashboard";
      })
      .catch(() => {
        setStatus("error");
      });
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: "#05070a" }}>
      <div className="w-full max-w-md text-center">
        <Link href="/">
          <img src={vexisLogo} alt="VEXIS" className="h-12 w-auto mx-auto mb-8 cursor-pointer" />
        </Link>

        <div className="rounded-2xl p-8" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }}>
          {status === "loading" ? (
            <div className="space-y-4">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-indigo-400" />
              <h3 className="text-lg font-semibold text-white">Verifying your link...</h3>
              <p className="text-sm" style={{ color: MUTED }}>Please wait while we sign you in.</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: "rgba(239,68,68,0.12)" }}>
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-white">This login link has expired</h3>
              <p className="text-sm" style={{ color: MUTED }}>
                Links are valid for 15 minutes. Request a new one below.
              </p>
              <Link href="/login">
                <Button className="w-full font-semibold text-white" style={{ backgroundColor: "#6366f1" }}
                  data-testid="button-get-new-link">
                  <Mail className="w-4 h-4 mr-2" />
                  Get a new link
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
